/**
 * JSON decoding at the I/O boundary.
 *
 * Everything in this app that comes from outside (the indexer's API, the binary
 * geometry files) is decoded into a typed value exactly once, here or in the
 * module that owns the format. After that boundary the rest of the code branches
 * on domain values, never on the shape of a raw payload.
 */

export type JsonValue = null | boolean | number | string | JsonValue[] | JsonObject;

export type JsonObject = { [key: string]: JsonValue };

export class DecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DecodeError";
  }
}

function isNumber(value: JsonValue): value is number {
  return Number.isFinite(value);
}

function isString(value: JsonValue): value is string {
  return typeof value === "string";
}

function isBoolean(value: JsonValue): value is boolean {
  return typeof value === "boolean";
}

function isObject(value: JsonValue): value is JsonObject {
  return (
    value !== null &&
    !Array.isArray(value) &&
    !isString(value) &&
    !isBoolean(value) &&
    !isNumber(value)
  );
}

export function asNumber(value: JsonValue, label: string): number {
  if (!isNumber(value))
    throw new DecodeError(`${label}: expected a number, got ${JSON.stringify(value)}`);

  return value;
}

/** A number the node may refuse to answer: null is "unknown", never a guess. */
export function optionalNumber(value: JsonValue, label: string): number | null {
  if (value === null || value === undefined) return null;

  return asNumber(value, label);
}

export function asString(value: JsonValue, label: string): string {
  if (!isString(value))
    throw new DecodeError(`${label}: expected a string, got ${JSON.stringify(value)}`);

  return value;
}

export function asBoolean(value: JsonValue, label: string): boolean {
  if (!isBoolean(value))
    throw new DecodeError(`${label}: expected a boolean, got ${JSON.stringify(value)}`);

  return value;
}

/** Reads one member of a decoded object. */
export function field(source: JsonValue, key: string, label: string): JsonValue {
  if (!isObject(source)) throw new DecodeError(`${label}: expected an object`);

  if (!Object.hasOwn(source, key)) throw new DecodeError(`${label}: missing "${key}"`);

  return source[key];
}

/** Same, but tolerates an absent member (treated as null). */
export function optionalField(source: JsonValue, key: string, label: string): JsonValue {
  if (!isObject(source)) throw new DecodeError(`${label}: expected an object`);

  return Object.hasOwn(source, key) ? source[key] : null;
}

export function asArray(value: JsonValue, label: string): JsonValue[] {
  if (!Array.isArray(value)) throw new DecodeError(`${label}: expected an array`);

  return value;
}

/** JSON.parse plus the one assertion that cannot go wrong: JSON.parse only ever
 *  produces JSON values. */
export function parseJson(text: string, label: string): JsonValue {
  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch (cause) {
    throw new DecodeError(`${label}: not JSON (${String(cause)})`);
  }

  // SAFETY: JSON.parse only ever yields JSON values, so this assertion cannot widen the type.
  return parsed as JsonValue;
}

export async function fetchJson(url: string): Promise<JsonValue> {
  const response = await fetch(url);

  if (!response.ok) throw new DecodeError(`${url}: HTTP ${response.status}`);

  return parseJson(await response.text(), url);
}
